// mapaAtividade.tsx - Mapa Completo de Atividades (IBIRACO) - COM LISTA DE COMUNICADOS
import React, { useEffect, useState, useCallback } from 'react';
import { db, storage, auth } from './firebaseConfig';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { 
  MapIcon, 
  ArrowLeft, 
  Megaphone, 
  Camera, 
  Trash2,
  AlertTriangle,
  CheckCircle,
  LogOut,
  List
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ✅ Centralizar em Sarzedo por padrão
const mapContainerStyle = { width: '100%', height: '100%', borderRadius: '1.5rem' };
const sarzedoCenter = { lat: -20.0315, lng: -44.1357 };

type Reporte = {
  id: string;
  latitude: number;
  longitude: number;
  endereco: string;
  descricao: string;
  imagemUrl: string;
  status: string;
  tipo: string;
  likes: number;
};

type Evento = {
  id: string;
  latitude: number;
  longitude: number;
  titulo: string;
  descricao: string;
  imagemUrl: string;
  likes: number;
  dataExpiracao?: any;
  createdAt?: any;
};

export default function MapaAtividadeScreen() {
  const navigate = useNavigate();
  
  const [cidadeDisplay, setCidadeDisplay] = useState<string>('');
  const [cidadeNormalizada, setCidadeNormalizada] = useState<string>('');
  
  const [reportes, setReportes] = useState<Reporte[]>([]);
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [selectedMarker, setSelectedMarker] = useState<any>(null);
  const [posicaoClick, setPosicaoClick] = useState<any>(sarzedoCenter);
  
  // Estados do formulário
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  
  // 🆕 Estado para alternar entre Criar e Lista
  const [abaForm, setAbaForm] = useState<'criar' | 'lista'>('criar');
  
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyABZxmhXkJXEdzImk166MYRcMU4fyIRB4I"
  });

  // ============================================================================
  // 🔄 INICIALIZAÇÃO
  // ============================================================================
  
  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (!user) {
        setAuthError(true);
        setLoading(false);
        navigate('/');
        return;
      }
      
      const cidadeDisplayLS = localStorage.getItem('cidadeDisplay');
      const cidadeNormalizadaLS = localStorage.getItem('cidadeNormalizada');
      
      if (cidadeDisplayLS && cidadeNormalizadaLS) {
        setCidadeDisplay(cidadeDisplayLS);
        setCidadeNormalizada(cidadeNormalizadaLS);
        setLoading(false);
      } else {
        const email = user.email || '';
        if (email.includes('mariocampos')) {
          setCidadeDisplay('Mário Campos');
          setCidadeNormalizada('mario_campos');
        } else if (email.includes('sarzedo')) {
          setCidadeDisplay('Sarzedo');
          setCidadeNormalizada('sarzedo');
        } else {
          setCidadeDisplay('Ibirité');
          setCidadeNormalizada('ibirite');
        }
        setLoading(false);
      }
    });
    
    return () => unsubscribeAuth();
  }, [navigate]);

  // ============================================================================
  // 🔄 MONITORAR DADOS
  // ============================================================================
  
  useEffect(() => {
    if (!cidadeNormalizada || loading) return;

    // Query de Reportes
    const cidadesBuscaReportes = cidadeNormalizada === "ibirite"
      ? ["ibirite", "Ibirité"]
      : [cidadeNormalizada];

    const qReportes = query(
      collection(db, "reportes"),
      where("cidadeReporte", "in", cidadesBuscaReportes)
    );
    
    const unsubReportes = onSnapshot(qReportes, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Reporte));
      setReportes(data);
    });

    // Query de Eventos
    const cidadesBuscaEventos = cidadeNormalizada === "ibirite"
      ? ["ibirite", "Ibirité", "IBIRITÉ"]
      : [cidadeNormalizada, cidadeDisplay];

    const qEventos = query(
      collection(db, "eventos_municipais"),
      where("cidade", "in", cidadesBuscaEventos)
    );
    
    const unsubEventos = onSnapshot(qEventos, (snapshot) => {
      const agora = new Date().getTime();
      const data = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() } as Evento))
        .filter(ev => {
          if (ev.dataExpiracao) {
            const expiracao = ev.dataExpiracao.seconds 
              ? ev.dataExpiracao.seconds * 1000
              : new Date(ev.dataExpiracao).getTime();
            return agora < expiracao;
          }
          if (ev.createdAt) {
            const dataCriacao = ev.createdAt.seconds * 1000;
            return (agora - dataCriacao) < (3 * 24 * 60 * 60 * 1000);
          }
          return true;
        });
      setEventos(data);
    });

    return () => {
      unsubReportes();
      unsubEventos();
    };
  }, [cidadeNormalizada, loading, cidadeDisplay]);

  // ============================================================================
  // 🎯 FUNÇÕES
  // ============================================================================

  const onMapClick = useCallback((e: any) => {
    setPosicaoClick({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, []);

  const criarEvento = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titulo || !image || !descricao) {
      return alert("Preencha título, descrição e foto!");
    }
    
    setEnviando(true);
    try {
      const storageRef = ref(storage, `eventos/${Date.now()}.jpg`);
      await uploadBytes(storageRef, image);
      const url = await getDownloadURL(storageRef);
      
      const dataExpiracao = new Date();
      dataExpiracao.setHours(dataExpiracao.getHours() + 72);
      
      await addDoc(collection(db, "eventos_municipais"), {
        titulo, 
        descricao, 
        cidade: cidadeDisplay,
        imagemUrl: url, 
        latitude: posicaoClick.lat, 
        longitude: posicaoClick.lng, 
        likes: 0,
        createdAt: serverTimestamp(),
        dataExpiracao: dataExpiracao
      });
      
      setTitulo(''); 
      setDescricao(''); 
      setImage(null);
      alert("Comunicado publicado! ✅");
    } catch (err) { 
      console.error(err); 
      alert("Erro ao publicar evento.");
    } finally { 
      setEnviando(false); 
    }
  };

  // ✅ NOVA FUNÇÃO: Deletar evento da lista
  const deletarEventoLista = async (id: string) => {
    if (window.confirm("Deseja realmente remover este comunicado?")) {
      try {
        await deleteDoc(doc(db, "eventos_municipais", id));
      } catch (err) {
        console.error(err);
        alert("Erro ao remover evento.");
      }
    }
  };

  // ============================================================================
  // 🎨 RENDERIZAÇÃO
  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F7FE] flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#e67e22]"></div>
      </div>
    );
  }

  if (authError || !cidadeNormalizada) {
    return (
      <div className="min-h-screen bg-[#F4F7FE] flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-3xl shadow-lg max-w-md">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-black text-gray-800 mb-2">Sessão expirada</h2>
          <button onClick={() => navigate('/')} className="bg-[#e67e22] text-white px-6 py-3 rounded-2xl font-bold">
            Ir para Login
          </button>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-[#F4F7FE] flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#e67e22]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7FE]">
      
      {/* HEADER */}
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/menu')} className="flex items-center gap-2 text-gray-600 hover:text-[#e67e22] font-bold">
                <ArrowLeft size={20} />
                <span>Voltar</span>
              </button>
              
              <div className="flex items-center gap-3">
                <div className="bg-[#e67e22] p-2 rounded-xl">
                  <MapIcon size={20} className="text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-black text-[#1A1C1D]">Mapa de Atividades</h1>
                  <p className="text-[10px] font-bold text-gray-500 uppercase">Prefeitura de {cidadeDisplay}</p>
                </div>
              </div>
            </div>

            <button onClick={() => navigate('/menu')} className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-xl font-bold text-sm hover:bg-red-100">
              <LogOut size={18} />
              Sair
            </button>
          </div>
        </div>
      </header>

      {/* CONTEÚDO PRINCIPAL - LADO A LADO */}
      <main className="max-w-[1600px] mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 h-[calc(100vh-100px)]">
          
          {/* 🔵 MAPA (3/4 da tela) */}
          <div className="lg:col-span-3 bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden">
            {/* Legenda */}
            <div className="p-3 bg-gray-50 border-b border-gray-100 flex flex-wrap gap-4 items-center justify-between">
              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-xs font-bold text-gray-600">Reportes Pendentes</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-xs font-bold text-gray-600">Resolvidos</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                  <span className="text-xs font-bold text-gray-600">Eventos</span>
                </div>
              </div>
              <div className="text-xs font-bold text-gray-500">
                {reportes.length} reportes • {eventos.length} eventos
              </div>
            </div>

            {/* GoogleMap */}
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={sarzedoCenter}
              zoom={14}
              onClick={onMapClick}
              options={{ 
                disableDefaultUI: false,
                zoomControl: true,
                mapTypeControl: false,
                streetViewControl: false,
                styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }]
              }}
            >
              {/* 🔴 Marcador do Clique */}
              <Marker 
                position={posicaoClick}
                icon={{ 
                  url: 'https://cdn-icons-png.flaticon.com/512/9131/9131546.png', 
                  scaledSize: new (window as any).google.maps.Size(35, 35) 
                }}
              />

              {/* Reportes Pendentes */}
              {reportes.filter(r => r.status !== 'Resolvido').map((reporte) => (
                <Marker 
                  key={reporte.id} 
                  position={{ lat: reporte.latitude, lng: reporte.longitude }} 
                  icon={{ 
                    url: 'https://cdn-icons-png.flaticon.com/512/595/595067.png', 
                    scaledSize: new (window as any).google.maps.Size(28, 28) 
                  }}
                  onClick={() => setSelectedMarker({ ...reporte, isEvento: false })}
                />
              ))}

              {/* Reportes Resolvidos */}
              {reportes.filter(r => r.status === 'Resolvido').map((reporte) => (
                <Marker 
                  key={reporte.id} 
                  position={{ lat: reporte.latitude, lng: reporte.longitude }} 
                  icon={{ 
                    url: 'https://cdn-icons-png.flaticon.com/512/190/190411.png', 
                    scaledSize: new (window as any).google.maps.Size(24, 24) 
                  }}
                />
              ))}

              {/* Eventos */}
              {eventos.map((evento) => (
                <Marker 
                  key={evento.id} 
                  position={{ lat: evento.latitude, lng: evento.longitude }} 
                  icon={{ 
                    url: 'https://cdn-icons-png.flaticon.com/512/1673/1673188.png', 
                    scaledSize: new (window as any).google.maps.Size(32, 32) 
                  }}
                  onClick={() => setSelectedMarker({ ...evento, isEvento: true })}
                />
              ))}

              {/* InfoWindow */}
              {selectedMarker && (
                <InfoWindow 
                  position={{ lat: selectedMarker.latitude, lng: selectedMarker.longitude }} 
                  onCloseClick={() => setSelectedMarker(null)}
                >
                  <div className="text-center p-2 min-w-[180px]">
                    <img src={selectedMarker.imagemUrl} className="w-20 h-20 rounded-lg mb-2 mx-auto object-cover" alt="" />
                    {selectedMarker.isEvento ? (
                      <>
                        <p className="text-xs font-black uppercase mb-1">{selectedMarker.titulo}</p>
                        <button onClick={() => deletarEventoLista(selectedMarker.id)} className="text-[9px] text-red-600 font-bold">
                          <Trash2 size={12} className="inline" /> Excluir
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-xs font-bold uppercase mb-1">{selectedMarker.endereco}</p>
                        <span className={`text-[8px] px-2 py-1 rounded-full font-bold ${
                          selectedMarker.status === 'Pendente' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                        }`}>
                          {selectedMarker.status}
                        </span>
                      </>
                    )}
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </div>

          {/* 🟠 BARRA LATERAL (1/4 da tela) - COM ABAS */}
          <div className="lg:col-span-1 bg-white rounded-[2rem] shadow-sm border border-gray-100 overflow-hidden flex flex-col">
            
            {/* 🔹 ABAS: Criar | Lista */}
            <div className="flex border-b border-gray-100">
              <button 
                onClick={() => setAbaForm('criar')}
                className={`flex-1 py-3 px-2 text-xs font-black uppercase tracking-tight transition-all flex items-center justify-center gap-1 ${
                  abaForm === 'criar' 
                    ? 'bg-[#e67e22] text-white' 
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                <Megaphone size={14} /> Criar
              </button>
              <button 
                onClick={() => setAbaForm('lista')}
                className={`flex-1 py-3 px-2 text-xs font-black uppercase tracking-tight transition-all flex items-center justify-center gap-1 ${
                  abaForm === 'lista' 
                    ? 'bg-[#e67e22] text-white' 
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                }`}
              >
                <List size={14} /> Lista ({eventos.length})
              </button>
            </div>

            {/* Conteúdo da Aba - Scrollável */}
            <div className="flex-1 overflow-y-auto p-4">
              
              {/* === ABA: CRIAR COMUNICADO === */}
              {abaForm === 'criar' && (
                <form onSubmit={criarEvento} className="space-y-3">
                  <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                    <Megaphone size={18} className="text-[#e67e22]" />
                    <h2 className="text-sm font-black text-[#1A1C1D] uppercase">Novo Comunicado</h2>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-gray-700 uppercase mb-1">Título *</label>
                    <input 
                      value={titulo} 
                      onChange={e => setTitulo(e.target.value)} 
                      placeholder="Ex: Vacinação"
                      className="w-full p-2 bg-gray-50 rounded-lg font-bold text-xs outline-none border-2 border-transparent focus:border-orange-200"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-gray-700 uppercase mb-1">Descrição *</label>
                    <textarea 
                      value={descricao} 
                      onChange={e => setDescricao(e.target.value)} 
                      placeholder="Descreva o evento..."
                      className="w-full p-2 bg-gray-50 rounded-lg font-bold text-xs outline-none border-2 border-transparent focus:border-orange-200 h-20 resize-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-gray-700 uppercase mb-1">📍 Localização</label>
                    <div className="bg-gray-100 p-2 rounded-lg">
                      <p className="text-[9px] text-gray-500">Clique no mapa</p>
                      <p className="text-[8px] text-gray-400 mt-1">
                        {posicaoClick.lat.toFixed(4)}, {posicaoClick.lng.toFixed(4)}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-gray-700 uppercase mb-1">Foto *</label>
                    <label className="flex flex-col items-center justify-center p-3 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 cursor-pointer hover:bg-orange-50">
                      {image ? (
                        <>
                          <CheckCircle size={24} className="text-green-500 mb-1" />
                          <span className="text-[9px] font-black text-green-600 uppercase">Foto OK</span>
                        </>
                      ) : (
                        <>
                          <Camera size={24} className="text-gray-300 mb-1" />
                          <span className="text-[9px] font-black text-gray-400 uppercase">Selecionar</span>
                        </>
                      )}
                      <input type="file" className="hidden" accept="image/*" onChange={(e) => e.target.files && setImage(e.target.files[0])} />
                    </label>
                  </div>

                  <button 
                    type="submit"
                    disabled={enviando}
                    className="w-full bg-[#e67e22] text-white py-3 rounded-xl font-black uppercase text-xs tracking-widest shadow-lg hover:bg-orange-600 transition-all disabled:opacity-50"
                  >
                    {enviando ? 'Publicando...' : '📢 Publicar'}
                  </button>

                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={14} className="text-orange-600 mt-0.5 flex-shrink-0" />
                      <p className="text-[9px] text-orange-600">Expira após 72 horas (3 dias)</p>
                    </div>
                  </div>
                </form>
              )}

              {/* === ABA: LISTA DE COMUNICADOS === */}
              {abaForm === 'lista' && (
                <div>
                  <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
                    <List size={18} className="text-[#e67e22]" />
                    <h2 className="text-sm font-black text-[#1A1C1D] uppercase">Comunicados Ativos</h2>
                  </div>

                  {eventos.length === 0 ? (
                    <div className="text-center py-8">
                      <Megaphone size={40} className="mx-auto mb-3 text-gray-300" />
                      <p className="text-xs font-bold text-gray-500">Nenhum comunicado ativo</p>
                      <button 
                        onClick={() => setAbaForm('criar')}
                        className="mt-3 text-[10px] font-black text-[#e67e22] uppercase hover:underline"
                      >
                        Criar primeiro comunicado
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {eventos.map((evento) => (
                        <div key={evento.id} className="bg-gray-50 rounded-xl p-3 border border-gray-200 hover:border-orange-200 transition-all">
                          {/* Foto do evento */}
                          {evento.imagemUrl && (
                            <img 
                              src={evento.imagemUrl} 
                              className="w-full h-20 object-cover rounded-lg mb-2" 
                              alt={evento.titulo}
                            />
                          )}
                          
                          {/* Título */}
                          <p className="text-xs font-black text-gray-800 uppercase mb-1 truncate">
                            {evento.titulo}
                          </p>
                          
                          {/* Descrição curta */}
                          <p className="text-[9px] text-gray-500 mb-2 line-clamp-2">
                            {evento.descricao}
                          </p>
                          
                          {/* Data */}
                          {evento.createdAt && (
                            <p className="text-[8px] text-gray-400 mb-2">
                              Criado em: {new Date(evento.createdAt.seconds * 1000).toLocaleDateString('pt-BR')}
                            </p>
                          )}
                          
                          {/* Botão Excluir */}
                          <button 
                            onClick={() => deletarEventoLista(evento.id)}
                            className="w-full flex items-center justify-center gap-1 bg-red-50 text-red-600 py-2 rounded-lg text-[9px] font-black uppercase hover:bg-red-100 transition-all"
                          >
                            <Trash2 size={12} /> Excluir
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}